// frontend/src/pages/admin/AdminUsersPage.tsx
import { useEffect, useState, useCallback } from 'react';
import { authedFetch } from '../../services/admin.service';
import { Users, Ban, CheckCircle2, Trash2 } from 'lucide-react';

interface UserRow {
  id: string;
  full_name: string;
  role: string;
  age: number | null;
  xp: number;
  is_banned: boolean;
  created_at: string;
  school_name?: string | null;
  city?: string | null;
}

export function AdminUsersPage() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [banningId, setBanningId] = useState<string | null>(null);
  const [banReason, setBanReason] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const showMsg = (text: string, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3000);
  };

  const reload = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter) params.set('role', roleFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);

      const data = await authedFetch<{ users: UserRow[] }>(`/api/admin/users?${params}`);
      setUsers(data.users ?? []);
    } catch {
      showMsg('Kullanıcılar yüklenemedi.', false);
    } finally {
      setIsLoading(false);
    }
  }, [search, roleFilter, statusFilter]);

  useEffect(() => {
    const t = setTimeout(reload, 300);
    return () => clearTimeout(t);
  }, [reload]);

  async function handleBan(id: string) {
    try {
      await authedFetch(`/api/admin/users/${id}/ban`, {
        method: 'POST',
        body: JSON.stringify({ reason: banReason || 'Belirtilmedi' }),
      });
      setBanningId(null);
      setBanReason('');
      showMsg('Kullanıcı banlandı.');
      reload();
    } catch {
      showMsg('Ban işlemi başarısız.', false);
    }
  }

  async function handleUnban(id: string) {
    try {
      await authedFetch(`/api/admin/users/${id}/unban`, { method: 'POST' });
      showMsg('Ban kaldırıldı.');
      reload();
    } catch {
      showMsg('Unban işlemi başarısız.', false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Bu kullanıcıyı kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!')) return;
    try {
      await authedFetch(`/api/admin/users/${id}`, { method: 'DELETE' });
      showMsg('Kullanıcı silindi. Aynı e-posta ile tekrar kayıt olabilir.');
      setDeletingId(null);
      reload();
    } catch (err: any) {
      showMsg(err?.message || 'Silme işlemi başarısız.', false);
    }
  }

  const ROLE_TR: Record<string, string> = {
    student: 'Öğrenci',
    parent: 'Veli',
    admin: 'Admin',
    school: 'Okul',
  };

  const ROLE_COLOR: Record<string, string> = {
    student: 'primary',
    parent: 'success',
    admin: '',
    school: '',
  };

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          Kullanıcı Yönetimi <Users size={28} color="#00AEEF" />
        </h1>
        <p>Filtreleme, arama, ban/unban ve silme işlemleri</p>
      </header>

      {msg && (
        <div
          className="quiz-error-banner"
          style={{
            background: msg.ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            borderColor: msg.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)',
            color: msg.ok ? 'var(--secondary)' : 'var(--danger)',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <CheckCircle2 size={16} />
          {msg.text}
        </div>
      )}

      {/* Filtreler */}
      <div className="admin-filters" style={{ marginBottom: 20 }}>
        <input
          className="input"
          placeholder="İsme göre ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 200 }}
        />
        <select className="input" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">Tüm Roller</option>
          <option value="student">Öğrenci</option>
          <option value="parent">Veli</option>
          <option value="school">Okul</option>
          <option value="admin">Admin</option>
        </select>
        <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Tüm Durumlar</option>
          <option value="active">Aktif</option>
          <option value="banned">Banlı</option>
        </select>
        <span style={{ fontSize: 13, color: 'var(--gray)', alignSelf: 'center' }}>
          {users.length} kullanıcı
        </span>
      </div>

      {isLoading ? (
        <div className="courses-loading">Yükleniyor...</div>
      ) : users.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: 'var(--gray)' }}>
          Kullanıcı bulunamadı.
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Ad Soyad</th>
                <th>Rol</th>
                <th>Yaş</th>
                <th>XP</th>
                <th>Okul</th>
                <th>Durum</th>
                <th>Kayıt</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 600 }}>{u.full_name}</td>
                  <td>
                    <span className={`lesson-badge ${ROLE_COLOR[u.role] ?? ''}`}>
                      {ROLE_TR[u.role] ?? u.role}
                    </span>
                  </td>
                  <td>{u.age ?? '-'}</td>
                  <td>{u.xp.toLocaleString('tr-TR')}</td>
                  <td style={{ fontSize: 12, color: 'var(--gray)' }}>{u.school_name ?? '-'}</td>
                  <td>
                    {u.is_banned ? (
                      <span className="lesson-badge" style={{ background: 'rgba(239,68,68,0.15)', color: 'var(--danger)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Ban size={12} /> Banlı
                      </span>
                    ) : (
                      <span className="lesson-badge success" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <CheckCircle2 size={12} /> Aktif
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--gray)' }}>
                    {new Date(u.created_at).toLocaleDateString('tr-TR')}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      {/* Ban / Unban */}
                      {u.role !== 'admin' && (
                        <>
                          {u.is_banned ? (
                            <button className="lesson-btn secondary sm" onClick={() => handleUnban(u.id)}>
                              Banı Kaldır
                            </button>
                          ) : banningId === u.id ? (
                            <>
                              <input
                                className="input"
                                style={{ width: 110, padding: '4px 8px', fontSize: 12 }}
                                placeholder="Sebep..."
                                value={banReason}
                                onChange={(e) => setBanReason(e.target.value)}
                              />
                              <button
                                className="lesson-btn sm"
                                style={{ background: 'var(--danger)', color: 'white' }}
                                onClick={() => handleBan(u.id)}
                              >
                                Onayla
                              </button>
                              <button className="lesson-btn secondary sm" onClick={() => setBanningId(null)}>
                                İptal
                              </button>
                            </>
                          ) : (
                            <button className="lesson-btn secondary sm" onClick={() => setBanningId(u.id)}>
                              Banla
                            </button>
                          )}

                          {/* Sil */}
                          <button
                            className="lesson-btn sm"
                            style={{ background: 'rgba(239,68,68,0.15)', color: 'var(--danger)', border: '1px solid rgba(239,68,68,0.3)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            onClick={() => handleDelete(u.id)}
                          >
                            <Trash2 size={12} /> Sil
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
